import { SectionHeader } from "@/components/ui/SectionHeader";
import { CodeWindow } from "@/components/ui/CodeWindow";
import { ApiEndpointsTable, type ApiEndpointRow } from "@/components/ui/ApiEndpointsTable";
import { AnimatedSpan, Terminal, TypingAnimation } from "@/components/ui/terminal";
import { EditorWindow } from "@/components/ui/EditorWindow";

const SDK_CODE = `import {
  MBSCCheckoutProvider,
  ApplyForm,
  AccountCard,
} from "@mbsc-core/checkout";

export default function ApplyPage() {
  const apiBaseUrl = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3000";

  return (
    <MBSCCheckoutProvider apiBaseUrl={apiBaseUrl} credentials="include">
      <div>
        <ApplyForm />
        <AccountCard />
      </div>
    </MBSCCheckoutProvider>
  );
}`;

const REST_API_TERMINAL_LINES = {
  command: [
    "# Intent（日本円→ステーブルコイン）の作成  ※ Cookie セッション認証",
    "> curl -X POST https://api.mbsc.example.com/api/v1/intents \\",
    '  -H "Content-Type: application/json" \\',
    '  -b "session=<jwt>" \\',
    "  -d '{",
    '    "amountJpy": 10000,',
    '    "issueChain": "polygon",',
    '    "walletAddress": "0x...",',
    '    "bankAccount": {',
    '      "bankName": "みずほ銀行",',
    '      "bankAccountNumber": "1234567",',
    '      "bankAccountHolder": "ヤマダ タロウ"',
    "    }",
    "  }'",
  ],
  response: [
    "# レスポンス",
    "{",
    '  "intent": {',
    '    "id": "int_123",',
    '    "status": "PENDING_DEPOSIT",',
    '    "amountJpy": 10000,',
    '    "transferDetails": {',
    '      "bankCode": "0001",',
    '      "branchCode": "001",',
    '      "accountNumber": "1234567",',
    '      "accountHolder": "MBSC ...",',
    '      "ref": "MBSC-..."',
    "    },",
    '    "expiresAt": "2026-02-14T10:00:00.000Z"',
    "  }",
    "}",
  ],
} as const;

const API_ROWS: ApiEndpointRow[] = [
  ["POST", "/api/v1/intents", "日本円→ステーブルコイン：Intent（発行申請）の作成"],
  ["GET", "/api/v1/intents/:id", "Intent のステータス取得"],
  ["POST", "/api/v1/intents/:id/payout", "入金確認済み Intent への ステーブルコイン 送金を実行"],
  ["POST", "/api/v1/redemptions", "ステーブルコイン→日本円：Redemption（払い戻し申請）の作成"],
  ["GET", "/api/v1/redemptions/:id", "Redemption のステータス取得"],
  ["POST", "/api/v1/redemptions/:id/payout", "入金確定済み Redemption の JPY 払い戻しを実行"],
  ["POST", "/api/v1/webhooks/deposit", "銀行入金通知の受信（内向き Webhook）"],
  ["POST", "/api/v1/webhooks/crypto-payin", "オンチェーン入金通知の受信（内向き Webhook）"],
  ["POST", "/api/v1/sandbox/simulate-deposit", "テスト用の入金シミュレーション"],
];

export function MbscDevelopers() {
  return (
    <section
      id="mbsc-developers"
      className="border-t border-border/60 bg-muted/20 px-6 py-20 md:px-12"
    >
      <div className="mx-auto max-w-4xl">
        <SectionHeader
          eyebrow="For Developers"
          title="MBSC API / MBSC React SDK"
          description="REST API（Cookie セッション認証: JWT）と、React SDK の両方から
            JPY ↔ ステーブルコイン のフローを実装できます。"
        />

        <div className="mt-12 space-y-16">
          <section aria-labelledby="mbsc-core-api">
            <h3
              id="mbsc-core-api"
              className="font-mono text-sm uppercase tracking-widest text-muted-foreground"
            >
              MBSC API
            </h3>
            <p className="mt-6 max-w-2xl text-muted-foreground leading-relaxed">
              Intent（日本円→ステーブルコイン）/ Redemption（ステーブルコイン→日本円）を REST API で操作し、入金/送金の状態遷移を
              アプリケーションに統合できます。
            </p>
            <Terminal className="mt-8">
              {REST_API_TERMINAL_LINES.command.map((line) =>
                line.startsWith("#") ? (
                  <AnimatedSpan key={line} className="text-muted-foreground">
                    {line}
                  </AnimatedSpan>
                ) : (
                  <TypingAnimation
                    key={line}
                    duration={10}
                    className={line.startsWith(">") ? "text-emerald-300" : "text-zinc-100/90"}
                  >
                    {line.startsWith(">") ? line.replace(/^>\s*/, "$ ") : line}
                  </TypingAnimation>
                ),
              )}
              <AnimatedSpan className="mt-2 block opacity-0"> </AnimatedSpan>
              {REST_API_TERMINAL_LINES.response.map((line, idx) => (
                <AnimatedSpan
                  key={`${idx}-${line}`}
                  className={
                    line.startsWith("#")
                      ? "text-zinc-400/80"
                      : line.trim().startsWith('"')
                        ? "text-sky-200/85"
                        : "text-zinc-100/80"
                  }
                >
                  {line}
                </AnimatedSpan>
              ))}
            </Terminal>
            <ApiEndpointsTable rows={API_ROWS} title="主要 API エンドポイント" className="mt-12" />
          </section>

          <section aria-labelledby="mbsc-core-react-sdk">
            <h3
              id="mbsc-core-react-sdk"
              className="font-mono text-sm uppercase tracking-widest text-muted-foreground"
            >
              MBSC React SDK
            </h3>
            <p className="mt-6 max-w-2xl text-muted-foreground leading-relaxed">
              Checkout を React コンポーネントとして組み込めます。状態完了時のコールバックで Intent を
              取得し、画面遷移や完了表示へつなげられます。
            </p>
            <EditorWindow filename="App.tsx" code={SDK_CODE} className="mt-8" language="TSX" />
          </section>
        </div>
      </div>
    </section>
  );
}
